import type { API } from 'geoview-core/api/api';
import type { MapViewer } from 'geoview-core/geo/map/map-viewer';
import { GVAbstractTestSuite } from './abstract-gv-test-suite';
import { MapConfigTester } from '../testers/map-config-tester';
import { MapTester } from '../testers/map-tester';
import { GVAbstractTester } from '../testers/abstract-gv-tester';
import type { ControllerRegistry } from 'geoview-core/core/controllers/base/controller-registry';

/**
 * The GeoView Test Suite for Map Configuration.
 */
export class GVTestSuiteMapConfig extends GVAbstractTestSuite {
  /** The Map Config Tester used in this Test Suite */
  #mapConfigTester: MapConfigTester;

  /** Viewport API tester using the same replace-map lifecycle as the config tests. */
  #mapTester: MapTester;

  /**
   * Constructs the Test Suite.
   *
   * @param api - The shared api
   * @param mapViewer - The map viewer
   * @param controllerRegistry - The controller registry
   */
  constructor(api: API, mapViewer: MapViewer, controllerRegistry: ControllerRegistry) {
    super(api, mapViewer, controllerRegistry);

    // Create the Map Config tester
    this.#mapConfigTester = new MapConfigTester(api, mapViewer, controllerRegistry);
    this.addTester(this.#mapConfigTester);
    this.#mapTester = new MapTester(api, mapViewer, controllerRegistry);
    this.addTester(this.#mapTester);
  }

  /**
   * Returns the name of the Test Suite.
   *
   * @returns The name of the Test Suite
   */
  override getName(): string {
    return 'Map Config Test Suite';
  }

  /**
   * Returns the description of the Test Suite.
   *
   * @returns The description of the Test Suite
   */
  override getDescriptionAsHtml(): string {
    return `Tests map configuration overrides by creating fresh map instances:<br/>
      <b>Footer/App bar</b> — Tab selection, default/empty configs<br/>
      <b>Nav bar</b> — Default controls, empty array fallback<br/>
      <b>View settings</b> — Zoom constraints, initialView vs homeView, home button navigation<br/>
      <b>Viewport</b> — Boundary samples, midpoint fitting, rotation, padding, resize, and invalid inputs<br/>
      <b>Components</b> — Overview map (present/absent/hideOnZoom/reprojection), North arrow<br/>
      <b>Overlays</b> — Point markers from config<br/>
      <b>initialSettings</b> — Controls (all false, remove cascading), states (visible, opacity, queryable, hoverable),<br/>
      &nbsp;&nbsp;legend collapsed, filters (GeoJSON, OGC Feature, WFS, ESRI Dynamic, ESRI Feature),<br/>
      &nbsp;&nbsp;opacity parent-child capping, runtime parent opacity change`;
  }

  /**
   * Gets the number of active tests launched by the full suite.
   *
   * @returns The number of active full-suite tester calls, excluding debug-only calls
   */
  override getTestsTotalFinal(): number {
    return 57;
  }

  /**
   * Overrides the debug hook for running a subset of tests during development.
   *
   * GV DEBUG SECTION TO NOT HAVE TO TEST EVERYTHING EVERYTIME, search for DEBUG_RUN_ONLY_DEBUG_FUNCTION for the flag.
   *
   * @returns A promise that resolves when the debug tests are completed
   */
  protected override onLaunchTestSuiteDEBUG(): Promise<unknown> {
    // Test DEBUG
    const pDevTest0 = this.#mapConfigTester.testFixedHeightWithCollapsedFooterBar();

    // Resolve when all
    return Promise.all([pDevTest0]);
  }

  /**
   * Overrides the implementation to perform the tests for this Test Suite.
   *
   * @returns A promise that resolves when tests are completed
   */
  protected override async onLaunchTestSuite(): Promise<unknown> {
    // Viewport tests replace this suite's map, never the map-functions suite's configured layers.
    await this.#mapTester.testFitViewportToMidpointsLonLat(3978, Math.PI / 4, 'dynamic', 'none', false, undefined, false);
    await this.#mapTester.testFitViewportToMidpointsLonLat(3857, 0, 'dynamic', 'none', true, undefined, false);
    await this.#mapTester.testFitViewportToMidpointsLonLat(3978, Math.PI / 4);
    await this.#mapTester.testFitViewportToMidpointsLonLat(3978, Math.PI / 4, 'dynamic', 'resize');
    await this.#mapTester.testFitViewportToMidpointsLonLat(3978, Math.PI / 4, 'dynamic', 'projection');
    await this.#mapTester.testFitViewportToMidpointsLonLat(3857, 0, 'dynamic', 'none', true, GVAbstractTester.VIEWPORT_ANTIMERIDIAN_LONLAT);
    await this.#mapTester.testInvalidViewportMidpoints();
    await this.#mapTester.testErrorFitViewportMidpointsCancelled();
    await this.#mapTester.testErrorFitViewportUnavailable();
    await this.#mapTester.testViewportBoundaryLonLat(3978);
    await this.#mapTester.testViewportBoundaryLonLat(3978, Math.PI / 4);
    await this.#mapTester.testViewportBoundaryLonLat(3857);
    await this.#mapTester.testViewportBoundaryLonLat(3978, 0, 'static');
    await this.#mapTester.testViewportBoundaryLonLat(3857, 0, 'dynamic', GVAbstractTester.VIEWPORT_ANTIMERIDIAN_LONLAT);
    await this.#mapTester.testViewportBoundaryPaddingAndResize();
    await this.#mapTester.testViewportBoundaryUnavailable();

    // Test fixed-height map layout without footer bar
    const pFixedHeightWithoutFooterBar = this.#mapConfigTester.testFixedHeightWithoutFooterBar();
    await pFixedHeightWithoutFooterBar;

    // Test fixed-height map layout with collapsed footer bar
    const pFixedHeightWithCollapsedFooterBar = this.#mapConfigTester.testFixedHeightWithCollapsedFooterBar();
    await pFixedHeightWithCollapsedFooterBar;

    // Test data table pre-loaded in footer bar
    const pDataTableInFooterBar = this.#mapConfigTester.testDataTableSelectedTabFooterBar();
    await pDataTableInFooterBar;

    // Test data table pre-loaded in app bar
    const pDataTableInAppBar = this.#mapConfigTester.testDataTableSelectedTabAppBar();
    await pDataTableInAppBar;

    // Test selectedLayersLayerPath referencing a GeoCore layer by UUID resolves to its first layer path (#3633)
    const pSelectedLayersLayerPathGeoCore = this.#mapConfigTester.testSelectedLayersLayerPathGeoCoreResolution();
    await pSelectedLayersLayerPathGeoCore;

    // Test no footerBar/appBar config has defaults
    const pNoFooterBarAppBarDefaults = this.#mapConfigTester.testNoFooterBarAppBarConfigHasDefaults();
    await pNoFooterBarAppBarDefaults;

    // Test empty footerBar/appBar tabs has no footer or app bar
    const pEmptyFooterBarAppBar = this.#mapConfigTester.testEmptyFooterBarAppBarTabsHasNoFooter();
    await pEmptyFooterBarAppBar;

    // Test panels declared in both appBar and footerBar are removed from the footer bar
    const pDuplicatedPanelsRemovedFromFooter = this.#mapConfigTester.testDuplicatedPanelsRemovedFromFooter();
    await pDuplicatedPanelsRemovedFromFooter;

    // Test navBar with null has defaults
    const pNoNavBarDefaults = this.#mapConfigTester.testNoNavBarHasDefaults();
    await pNoNavBarDefaults;

    // Test navBar with empty components has zoom and rotate
    const pEmptyNavBarZoomRotate = this.#mapConfigTester.testEmptyNavBarHasZoomRotate();
    await pEmptyNavBarZoomRotate;

    // Test initial view with layerIds sets extent
    const pInitialViewLayerIds = this.#mapConfigTester.testInitialViewLayerIdsSetExtent();
    await pInitialViewLayerIds;

    // Test overlay objects with point markers
    const pOverlayObjectsPointMarkers = this.#mapConfigTester.testOverlayObjectsPointMarkers();
    await pOverlayObjectsPointMarkers;

    // Test view settings zoom constraints
    const pViewSettingsZoomConstraints = this.#mapConfigTester.testViewSettingsZoomConstraints();
    await pViewSettingsZoomConstraints;

    // Test initial view vs home view (zoom difference)
    const pInitialViewVsHomeView = this.#mapConfigTester.testViewSettingsInitialViewVsHomeView();
    await pInitialViewVsHomeView;

    // Test home button navigates to homeView center coordinates
    const pHomeButtonNavigatesToHomeView = this.#mapConfigTester.testViewSettingsHomeButtonNavigatesToHomeView();
    await pHomeButtonNavigatesToHomeView;

    // Test overview map is present when configured
    const pOverviewMapPresent = this.#mapConfigTester.testOverviewMapPresent();
    await pOverviewMapPresent;

    // Test overview map is absent when not configured
    const pOverviewMapAbsent = this.#mapConfigTester.testOverviewMapAbsent();
    await pOverviewMapAbsent;

    // Test overview map hideOnZoom behavior
    const pOverviewMapHideOnZoom = this.#mapConfigTester.testOverviewMapHideOnZoom();
    await pOverviewMapHideOnZoom;

    // Test overview map hideOnZoom with reprojection
    const pOverviewMapHideOnZoomReproject = this.#mapConfigTester.testOverviewMapHideOnZoomWithReprojection();
    await pOverviewMapHideOnZoomReproject;

    // Test north arrow is present when configured
    const pNorthArrowPresent = this.#mapConfigTester.testNorthArrowPresent();
    await pNorthArrowPresent;

    // Test north arrow is absent when not configured
    const pNorthArrowAbsent = this.#mapConfigTester.testNorthArrowAbsent();
    await pNorthArrowAbsent;

    // Test initialSettings all controls set to false
    const pControlsAllFalse = this.#mapConfigTester.testInitialSettingsControlsAllFalse();
    await pControlsAllFalse;

    // Test initialSettings states
    const pStateVisible = this.#mapConfigTester.testInitialSettingsStateVisibleFalse();
    await pStateVisible;

    const pStateOpacity = this.#mapConfigTester.testInitialSettingsStateOpacity();
    await pStateOpacity;

    const pStateQueryable = this.#mapConfigTester.testInitialSettingsStateQueryableFalse();
    await pStateQueryable;

    const pStateHoverable = this.#mapConfigTester.testInitialSettingsStateHoverableFalse();
    await pStateHoverable;

    // Test initialSettings controls + states combo
    const pComboQueryControlTrueStateQueryableFalse = this.#mapConfigTester.testInitialSettingsComboQueryControlTrueStateQueryableFalse();
    await pComboQueryControlTrueStateQueryableFalse;

    const pComboHoverControlTrueStateHoverableFalse = this.#mapConfigTester.testInitialSettingsComboHoverControlTrueStateHoverableFalse();
    await pComboHoverControlTrueStateHoverableFalse;

    // Test controls.remove cascading through group hierarchy
    const pRemoveCascading = this.#mapConfigTester.testInitialSettingsControlRemoveCascadingToDescendants();
    await pRemoveCascading;

    // Test states.visible cascading through group hierarchy
    const pVisibleCascading = this.#mapConfigTester.testInitialSettingsStateVisibleCascadingToDescendants();
    await pVisibleCascading;

    // Test opacity cascading with parent/child layers
    const pOpacityCappedByParent = this.#mapConfigTester.testInitialSettingsOpacityCascadingChildCappedByParent();
    await pOpacityCappedByParent;

    const pOpacityBelowParent = this.#mapConfigTester.testInitialSettingsOpacityCascadingChildBelowParent();
    await pOpacityBelowParent;

    const pOpacityRuntimeCascade = this.#mapConfigTester.testInitialSettingsOpacityCascadingRuntimeParentChange();
    await pOpacityRuntimeCascade;

    // Test legend collapsed initial state
    const pLegendCollapsed = this.#mapConfigTester.testInitialSettingsLegendCollapsed();
    await pLegendCollapsed;

    // Test parent controls cascade to children (all controls)
    const pControlsCascade = this.#mapConfigTester.testInitialSettingsControlsCascadeToChildren();
    await pControlsCascade;

    // Test group-level control override
    const pControlsGroupOverride = this.#mapConfigTester.testInitialSettingsControlsGroupOverride();
    await pControlsGroupOverride;

    // Test initial filter on GeoJSON layer
    const pInitialFilters = this.#mapConfigTester.testInitialSettingsFilters();
    await pInitialFilters;

    // Test initial filter on OGC Feature layer
    const pInitialFiltersOgcFeature = this.#mapConfigTester.testInitialSettingsFiltersOgcFeature();
    await pInitialFiltersOgcFeature;

    // Test initial filter on WFS layer
    const pInitialFiltersWfs = this.#mapConfigTester.testInitialSettingsFiltersWfs();
    await pInitialFiltersWfs;

    // Test initial filter on Esri Dynamic layer
    const pInitialFiltersEsriDynamic = this.#mapConfigTester.testInitialSettingsFiltersEsriDynamic();
    await pInitialFiltersEsriDynamic;

    // Test initial filter on Esri Feature layer
    const pInitialFiltersEsriFeature = this.#mapConfigTester.testInitialSettingsFiltersEsriFeature();
    await pInitialFiltersEsriFeature;

    // Resolve when all
    return Promise.all([
      pDataTableInFooterBar,
      pDataTableInAppBar,
      pNoFooterBarAppBarDefaults,
      pEmptyFooterBarAppBar,
      pDuplicatedPanelsRemovedFromFooter,
      pNoNavBarDefaults,
      pEmptyNavBarZoomRotate,
      pInitialViewLayerIds,
      pOverlayObjectsPointMarkers,
      pViewSettingsZoomConstraints,
      pInitialViewVsHomeView,
      pHomeButtonNavigatesToHomeView,
      pOverviewMapPresent,
      pOverviewMapAbsent,
      pNorthArrowPresent,
      pNorthArrowAbsent,
      pOverviewMapHideOnZoom,
      pOverviewMapHideOnZoomReproject,
      pControlsAllFalse,
      pStateVisible,
      pStateOpacity,
      pStateQueryable,
      pStateHoverable,
      pComboQueryControlTrueStateQueryableFalse,
      pComboHoverControlTrueStateHoverableFalse,
      pRemoveCascading,
      pVisibleCascading,
      pOpacityCappedByParent,
      pOpacityBelowParent,
      pOpacityRuntimeCascade,
      pLegendCollapsed,
      pControlsCascade,
      pControlsGroupOverride,
      pInitialFilters,
      pInitialFiltersOgcFeature,
      pInitialFiltersWfs,
      pInitialFiltersEsriDynamic,
      pInitialFiltersEsriFeature,
    ]);
  }
}
